import { z } from 'zod';
/**
 * Contratto dati del singolo brand (Specifica JSON v2.4), regole [CRITICA]
 * comprese. Unità di import: un oggetto brand. Il validatore controlla forma e
 * coerenza interna, mai il merito di un indice (§0.1: "mappa, non calcola").
 * Ogni campo di testo è `prose()` o `htmlBlock()`: è la classificazione
 * prosa/marcatura, e decide come si normalizza l'em dash.
 */
/** §4 — elenco chiuso degli id piattaforma. */
export const PLATFORM_IDS = [
    'trustpilot',
    'google',
    'feedaty',
    'recensioni-verificate',
    'trustmate',
    'trusted-shops',
    'glassdoor',
];
export const SIGNAL_QUALITIES = ['solid', 'partial', 'insufficient'];
/**
 * §2 — `famecertStatus`. Divergenza dichiarata (funzionale §17 C5): un valore
 * fuori elenco è rifiutato, non reso come `none` come dice la v2.4. Assente →
 * default `none`.
 */
export const FAMECERT_STATUSES = ['none', 'certificato'];
/**
 * §2 — elenco chiuso di `fameIndex.label`.
 *
 * Dal 16/10 (Level 1 punto 5) l'etichetta non si legge più dal JSON: la
 * dashboard la ricava dal valore (`fameIndexLabel` in `derived.ts`). L'elenco
 * resta come validazione di forma, e il 23/10 (punto 5) ha
 * chiesto di ammettere anche le due voci della scala nuova che mancavano,
 * tenendo quelle vecchie «perché i file già caricati restino validi».
 *
 * Nessun ordine da rispettare: l'elenco alimenta solo `z.enum`, non una scala.
 */
export const FAME_INDEX_LABELS = [
    // Scala in uso, dal valore più basso (16/10, Level 1 punto 5).
    'Basso',
    'Nella media',
    'Buono',
    'Ottimo',
    'Eccellente',
    // Valore assente.
    'N.D.',
    // Scala precedente, ancora ammessa: invalidarla respingerebbe i file già
    // consegnati (23/10).
    'Insufficiente',
    'Sufficiente',
];
export const FAME_RISK_LABELS = ['Basso', 'Moderato', 'Rilevante', 'Alto', 'Critico'];
export const MATRIX_LABELS = ['Conforme', 'Non Conforme', 'Non Strutturato'];
/**
 * §6 — colore del verdetto: `green` Conforme, `red` Non Conforme, `blue` Non
 * Strutturato. `amber` è nell'elenco perché la specifica lo dichiara tollerato
 * ("mappato a orange"), non perché i dati lo usino: nel dataset non compare.
 * La mappatura verso `orange` è resa dal front-end, che è dove serve.
 */
export const MATRIX_COLORS = ['green', 'red', 'blue', 'amber'];
export const TOUCHPOINT_STATUSES = ['presidiato', 'parziale', 'assente'];
export const TRENDS = ['up', 'stable', 'down'];
export const FORUM_SENTIMENTS = ['positivo', 'neutro', 'negativo'];
/**
 * §4bis — chiavi di `intelligence` rese dal blocco 05 come riga etichetta/valore,
 * le sole su cui si normalizza l'em dash. Allineato a `dashboard/PlatformCards.tsx`
 * (verificato da un test). `nota` e `temiNegativi` non sono righe di testo.
 */
export const INTELLIGENCE_RENDERED_KEYS = [
    'poolProprio',
    'valutazioneProdotto',
    'scoreEsposto',
    'riconciliazione',
    'avvioRaccolta',
    'presidio',
    'asse',
    'genuinoAttesoVerticale',
    'patternTesti',
    'deterioramentoDatato',
    'flag',
];
/** §6 — casi di classificazione: 01-10 più 02b, sempre stringa a due cifre. */
export const CASI = ['01', '02', '02b', '03', '04', '05', '06', '07', '08', '09', '10'];
/** §8 — tetto di 8 snapshot (2 anni di trimestri). */
export const MAX_HISTORY_SNAPSHOTS = 8;
/**
 * §0.5 — em dash nella prosa: normalizzato, non rifiutato. Diventa virgola,
 * salvo a inizio/fine testo o dopo punteggiatura, dove sparisce.
 */
export function stripEmDash(value) {
    if (!value.includes('—'))
        return value;
    return value.replace(EM_DASH_RUN, (match, offset, full) => sostituzione(full.slice(0, offset), full.slice(offset + match.length)));
}
/** Il trattino lungo e gli spazi che si porta dietro. */
const EM_DASH_RUN = /\s*—\s*/g;
/**
 * Che cosa mettere al posto del trattino, viste le due parti di testo che lo
 * circondano. Regola unica per prosa e marcatura.
 */
function sostituzione(prima, dopo) {
    // A inizio o fine testo il trattino sparisce e basta: una virgola lì sarebbe
    // punteggiatura inventata.
    if (prima.trim() === '' || dopo.trim() === '')
        return '';
    // Se il testo precedente porta già la sua punteggiatura, resta lo spazio.
    if (/[,.;:!?]\s*$/.test(prima))
        return ' ';
    return ', ';
}
/**
 * Tag, commento o dichiarazione: tutto ciò che il browser **non** stampa.
 * Le virgolette sono rispettate perché un valore di attributo può contenere
 * `>` senza chiudere il tag.
 */
const TAG_O_COMMENTO = /<!--[\s\S]*?-->|<[!/?]?[a-zA-Z][^>"']*(?:(?:"[^"]*"|'[^']*')[^>"']*)*>/g;
/**
 * Em dash nei campi di marcatura: stessa regola, applicata al solo testo.
 * Dentro tag, commenti e dichiarazioni non si tocca nulla (`href`, `style`,
 * `data-*` sono valori, non punteggiatura); il contesto "inizio/fine testo" è
 * la riga visibile, delimitata dai tag di blocco, non la stringa intera.
 */
export function stripEmDashInMarkup(value) {
    if (!EM_DASH_MARKUP_PRESENTE.test(value))
        return value;
    const fuoriTesto = [];
    for (const tag of value.matchAll(TAG_O_COMMENTO)) {
        const inizio = tag.index ?? 0;
        fuoriTesto.push([inizio, inizio + tag[0].length]);
    }
    return value.replace(EM_DASH_RUN_MARKUP, (match, offset, full) => {
        const fine = offset + match.length;
        // Dentro un tag, un commento o una dichiarazione non si tocca nulla.
        if (fuoriTesto.some(([da, a]) => offset < a && fine > da))
            return match;
        return sostituzione(testoPrima(full, offset), testoDopo(full, fine));
    });
}
/** Nel markup l'em dash può essere carattere o entità; nella prosa solo carattere. */
const EM_DASH_MARKUP = String.raw `—|&mdash;|&#8212;|&#[xX]2014;`;
const EM_DASH_RUN_MARKUP = new RegExp(String.raw `\s*(?:${EM_DASH_MARKUP})\s*`, 'g');
const EM_DASH_MARKUP_PRESENTE = new RegExp(EM_DASH_MARKUP);
/**
 * Tag di blocco: oltre uno di questi il testo va a capo. I tag inline (`strong`,
 * `em`, `a`, `span`…) sono trasparenti di proposito.
 */
const TAG_DI_BLOCCO = /<(?:br|hr)\b[^>]*>|<\/?(?:p|div|li|ul|ol|dl|dt|dd|td|th|tr|table|thead|tbody|tfoot|h[1-6]|section|article|header|footer|blockquote|pre|figure|figcaption|svg|g|text)\b[^>]*>/gi;
/** Testo che il lettore vede prima del trattino, entro la riga corrente. */
function testoPrima(full, fino) {
    const prefisso = full.slice(0, fino);
    let da = 0;
    for (const blocco of prefisso.matchAll(TAG_DI_BLOCCO)) {
        da = (blocco.index ?? 0) + blocco[0].length;
    }
    return prefisso.slice(da).replace(TAG_O_COMMENTO, '');
}
/** Testo che il lettore vede dopo il trattino, entro la riga corrente. */
function testoDopo(full, da) {
    const suffisso = full.slice(da);
    let fino = suffisso.length;
    for (const blocco of suffisso.matchAll(TAG_DI_BLOCCO)) {
        fino = blocco.index ?? suffisso.length;
        break;
    }
    return suffisso.slice(0, fino).replace(TAG_O_COMMENTO, '');
}
/** Campo di prosa: tetto di lunghezza (difesa da file malformati), em dash normalizzato. */
const prose = (max) => z.string().max(max).transform(stripEmDash);
/**
 * Tetto dei campi di marcatura: difesa contro un file malformato (la v2.4 §11
 * non ne prevede). Deve restare sotto il limite del body della rotta di import
 * (1 MB), così l'eccesso produce un 422 sul campo e non un errore del parser.
 */
const MAX_MARKUP = 100_000;
/**
 * Campo di marcatura (v2.4 §11): HTML già formattato, reso con
 * `dangerouslySetInnerHTML`. Divergenza dichiarata: l'em dash si normalizza
 * anche qui, nel solo testo. Il codice eseguibile lo toglie il backend prima
 * della scrittura (`brands/sanitize.ts`), non il validatore.
 */
const htmlBlock = (max = MAX_MARKUP) => z.string().max(max).transform(stripEmDashInMarkup);
/** §0.4 — euro interi, mai abbreviazioni: `1500000`, non `1.5M`. */
const euro = z.int().min(0).max(100_000_000_000);
/** §4 — le percentuali delle stelle sono decimali: `20.7`, `3.9`. */
const percent = z.number().min(0).max(100);
/**
 * §7.6 — la percentuale dei temi sentiment è invece un **intero**, e il decimale
 * è quello dell'intelligence (`temiNegativi: { assistenza: 23.3 }`), che è un
 * campo diverso e resta non tipizzato perché schema-less.
 */
const sentimentPct = z.int().min(0).max(100);
// ---------------------------------------------------------------------------
// §4 platforms
// ---------------------------------------------------------------------------
/**
 * §4bis — `intelligence` è schema-less: si valida solo che sia un oggetto e
 * nessuna chiave viene filtrata. L'em dash si normalizza solo sulle chiavi rese
 * dal blocco 05 (`INTELLIGENCE_RENDERED_KEYS`, regola della prosa; `asse` è
 * [CRITICA]) e su `nota` (regola della marcatura). L'ordine delle chiavi è
 * informazione e viene conservato.
 */
const intelligenceSchema = z.record(z.string(), z.unknown()).transform((intelligence) => {
    const normalizzato = { ...intelligence };
    for (const key of INTELLIGENCE_RENDERED_KEYS) {
        const valore = normalizzato[key];
        if (typeof valore === 'string')
            normalizzato[key] = stripEmDash(valore);
    }
    const nota = normalizzato['nota'];
    if (typeof nota === 'string')
        normalizzato['nota'] = stripEmDashInMarkup(nota);
    return normalizzato;
});
const starsSchema = z.object({
    '1': percent,
    '2': percent,
    '3': percent,
    '4': percent,
    '5': percent,
});
export const platformSchema = z
    .object({
    id: z.enum(PLATFORM_IDS),
    name: z.string().min(1).max(80),
    available: z.boolean(),
    rating: z.number().min(0).max(5).nullable(),
    reviews: z.int().min(0),
    /** §4 — percentuali "1".."5"; `{}` vuoto quando available=false. */
    stars: z.union([starsSchema, z.object({}).strict()]),
    /** §4 — solo Trustpilot, percentuale 0-100 decimale. */
    basicLinkRate: z.number().min(0).max(100).optional(),
    intelligence: intelligenceSchema.optional(),
})
    .superRefine((p, ctx) => {
    // [CRITICA §4] Piattaforma non disponibile: rating null, reviews 0, stars {}
    // azzerata e NON omessa, per compatibilità di rendering.
    if (!p.available) {
        if (p.rating !== null) {
            ctx.addIssue({ code: 'custom', path: ['rating'], message: 'rating_deve_essere_null' });
        }
        if (p.reviews !== 0) {
            ctx.addIssue({ code: 'custom', path: ['reviews'], message: 'reviews_deve_essere_zero' });
        }
        if (Object.keys(p.stars).length !== 0) {
            ctx.addIssue({ code: 'custom', path: ['stars'], message: 'stars_deve_essere_vuoto' });
        }
        return;
    }
    if (p.rating === null) {
        ctx.addIssue({ code: 'custom', path: ['rating'], message: 'rating_obbligatorio' });
    }
    const stars = p.stars;
    const keys = Object.keys(stars);
    if (keys.length !== 5) {
        ctx.addIssue({ code: 'custom', path: ['stars'], message: 'stars_incomplete' });
    }
    else {
        // [CRITICA §0.3] Stelle sempre in percentuale, mai conteggi: somma ~100.
        // La tolleranza assorbe gli arrotondamenti a una cifra decimale.
        const sum = keys.reduce((acc, k) => acc + (stars[k] ?? 0), 0);
        if (Math.abs(sum - 100) > 1.5) {
            ctx.addIssue({
                code: 'custom',
                path: ['stars'],
                message: `stars_somma_non_100 (${sum.toFixed(2)})`,
            });
        }
    }
    // §4 — basicLinkRate esiste solo su Trustpilot.
    if (p.basicLinkRate !== undefined && p.id !== 'trustpilot') {
        ctx.addIssue({
            code: 'custom',
            path: ['basicLinkRate'],
            message: 'basiclinkrate_solo_su_trustpilot',
        });
    }
});
// ---------------------------------------------------------------------------
// §3 indices
// ---------------------------------------------------------------------------
export const indicesSchema = z.object({
    /**
     * [CRITICA v2.2] Valore WebScore prodotto da FameAI, trascritto tale e quale.
     * Può NON coincidere col rating della primaryPlatform: è vietato imporre
     * l'uguaglianza, ed è il motivo per cui qui non c'è alcun controllo incrociato.
     */
    webScore: z.number().min(0).max(5),
    /** [v2.3] Opzionale: omesso, non null, quando non calcolabile. */
    organicScore: z.number().min(0).max(5).optional(),
    fameIndex: z.object({
        value: z.number().min(0).max(5).nullable(),
        label: z.enum(FAME_INDEX_LABELS),
        /**
         * Motivazione del N.D. (v2.5): resa in calce alla card FameIndex quando
         * `value` è null; con un valore presente si conserva e non si mostra. Il
         * colore non ha campo: si deriva dal value (`fameIndexColor`).
         */
        note: prose(2000).optional(),
    }),
    /** §3 — intero 0-100 (17, non 0.17). Label e colore sono derivati dal numero. */
    fameIntegrity: z.int().min(0).max(100).nullable(),
});
// ---------------------------------------------------------------------------
// §5-6 rischio, classificazione, evidenze
// ---------------------------------------------------------------------------
export const fameRiskSchema = z
    .object({
    level: z.int().min(1).max(5),
    label: z.enum(FAME_RISK_LABELS),
    /** Nessun `color` (v2.5): il colore del gauge si deriva dal livello. */
    sanctionMin: euro,
    sanctionMax: euro,
})
    .refine((r) => r.sanctionMax >= r.sanctionMin, {
    path: ['sanctionMax'],
    message: 'sanctionmax_minore_di_min',
});
export const classificationSchema = z.object({
    caso: z.enum(CASI),
    casoName: prose(200),
    /** §6 — verdetto di conformità, NON il quadrante matrice. Dal v40 non è più reso. */
    matrixLabel: z.enum(MATRIX_LABELS),
    /** §6 — elenco chiuso: il valore finisce in un nome di classe CSS (`brand-dot`). */
    matrixColor: z.enum(MATRIX_COLORS, { error: 'matrix_color_non_valido' }).optional(),
});
export const reputationalDamageSchema = z
    .object({
    available: z.boolean(),
    min: euro,
    max: euro,
    note: prose(2000).optional(),
})
    .superRefine((d, ctx) => {
    // [CRITICA §6] available=false: min e max valgono 0 (non omessi) e la nota
    // con la motivazione è obbligatoria.
    if (!d.available) {
        if (d.min !== 0 || d.max !== 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['min'],
                message: 'danno_non_disponibile_deve_essere_zero',
            });
        }
        if (!d.note || d.note.trim().length === 0) {
            ctx.addIssue({ code: 'custom', path: ['note'], message: 'nota_obbligatoria' });
        }
    }
    if (d.max < d.min) {
        ctx.addIssue({ code: 'custom', path: ['max'], message: 'max_minore_di_min' });
    }
});
// ---------------------------------------------------------------------------
// §8 history
// ---------------------------------------------------------------------------
export const historySnapshotSchema = z.object({
    month: z.string().min(2).max(20),
    webScore: z.number().min(0).max(5),
    fameIntegrity: z.int().min(0).max(100),
    fameIndex: z.number().min(0).max(5).nullable(),
    volume: z.int().min(0),
    /**
     * §8 — intero da 0 a 100: "90 significa 90%, non 0,9". Il tipo non è un
     * dettaglio, è la regola: un `0.9` accettato finirebbe nella trend card
     * "Reply rate" come `0.9%` invece di `90%`, e nessuno lo vedrebbe come errore.
     */
    replyRate: z.int().min(0).max(100),
    gap: z.number().nullable(),
});
/** §8 — "Q3 2026" oppure un mese; la cadenza non va mescolata dentro un brand. */
const QUARTER = /^Q[1-4] \d{4}$/;
// ---------------------------------------------------------------------------
// §7 level2 — blocchi Diagnosi
// ---------------------------------------------------------------------------
const blockBase = { title: prose(200), subtitle: prose(400) };
/** Colonna di una tabella strutturata; `numeric` → carattere monospaziato. */
const tableColumnSchema = z.object({
    label: prose(120),
    numeric: z.boolean().optional(),
});
/**
 * Blocco di `level2.*.blocks`: una tabella (occhiello in `caption`, celle come
 * stringhe già formattate, mai numeri) o un paragrafo con marcatura inline.
 * `paragraph.text` è l'unico pezzo di markup e passa dal sanificatore.
 */
const contentBlockSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('table'),
        caption: prose(200).optional(),
        columns: z.array(tableColumnSchema).min(1).max(12),
        rows: z
            .array(z.array(prose(400)))
            .min(1)
            .max(200),
    }),
    z.object({
        type: z.literal('paragraph'),
        text: htmlBlock(8_000),
    }),
]);
const contentBlocksSchema = z.array(contentBlockSchema).min(1).max(40);
/**
 * Tag rifiutati dentro `content`: tabelle e grafica passano solo dai campi
 * strutturati. `iframe`/`object`/`embed` non sono qui perché sono codice
 * eseguibile, competenza del sanificatore.
 */
const STRUCTURAL_TAGS = [
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
    'caption',
    'colgroup',
    'col',
    'svg',
    'canvas',
    'img',
    'picture',
    'video',
    'audio',
];
const STRUCTURAL_TAG = new RegExp(`<\\s*(${STRUCTURAL_TAGS.join('|')})\\b`, 'gi');
/** Tag strutturali presenti nel frammento, senza doppioni, in ordine di comparsa. */
function structuralTagsIn(markup) {
    const trovati = [];
    for (const m of markup.matchAll(STRUCTURAL_TAG)) {
        const tag = (m[1] ?? '').toLowerCase();
        if (!trovati.includes(tag))
            trovati.push(tag);
    }
    return trovati;
}
/**
 * Blocco Diagnosi con testo: `content` (prosa con marcatura inline, senza tag
 * strutturali) e/o `blocks`; almeno uno dei due.
 */
const contentBlockShape = {
    ...blockBase,
    content: htmlBlock().optional(),
    blocks: contentBlocksSchema.optional(),
};
function refineContentBlock(b, ctx) {
    if (b.content === undefined && b.blocks === undefined) {
        ctx.addIssue({ code: 'custom', path: ['content'], message: 'contenuto_mancante' });
    }
    if (b.content !== undefined) {
        const strutturali = structuralTagsIn(b.content);
        if (strutturali.length > 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['content'],
                message: `contenuto_strutturato_non_ammesso: ${strutturali.join(', ')}`,
            });
        }
    }
    for (const [i, blocco] of (b.blocks ?? []).entries()) {
        if (blocco.type !== 'table')
            continue;
        for (const [r, riga] of blocco.rows.entries()) {
            // Una riga corta, dentro un blob HTML, arriva a schermo storta e
            // nessuno se ne accorge: qui invece è un 422 che dice quale riga.
            if (riga.length !== blocco.columns.length) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['blocks', i, 'rows', r],
                    message: `celle_diverse_dalle_colonne: ${riga.length} invece di ${blocco.columns.length}`,
                });
            }
        }
    }
}
/**
 * §7.6 — tema del sentiment. `trend` è **facoltativo [EMENDATO 23/10]**: il
 * committente non misura l'andamento di ogni tema, e imporlo lo costringeva a
 * scrivere `stable` dove il dato non c'era, cioè a mostrare il simbolo del
 * "fermo" per un'informazione che non aveva. Assente, il simbolo non si rende
 * (`dashboard/decisionCards.tsx`). L'elenco chiuso non cambia, quindi i file
 * già caricati restano validi.
 */
const sentimentTheme = z.object({
    tema: prose(200),
    pct: sentimentPct,
    trend: z.enum(TRENDS).optional(),
});
export const level2Schema = z.object({
    mechanism: z
        .object({
        ...contentBlockShape,
        /** [CRITICA §7.1] stats è array di {label, value}, mai stringhe. */
        stats: z.array(z.object({ label: prose(120), value: prose(120) })).optional(),
    })
        .superRefine(refineContentBlock),
    touchpoint: z.object({
        ...blockBase,
        items: z.array(z.object({
            name: prose(200),
            status: z.enum(TOUCHPOINT_STATUSES),
            desc: prose(1000),
        })),
    }),
    verdict: z.object({
        ...blockBase,
        result: prose(2000),
        signals: z.array(prose(1000)),
    }),
    dichotomy: z.object(contentBlockShape).superRefine(refineContentBlock),
    /** §7.6 — condizionale: se non ci sono dati, il blocco va omesso. */
    sentiment: z
        .object({
        ...blockBase,
        positivi: z.array(sentimentTheme),
        negativi: z.array(sentimentTheme),
        /** v40 — nota in coda al blocco, resa come HTML. */
        nota: htmlBlock().optional(),
    })
        .optional(),
    /** §7.7 — solo con dati Reddit reali, altrimenti omesso. */
    forum: z
        .object({
        ...contentBlockShape,
        temi: z.array(z.object({ tema: prose(200), sentiment: z.enum(FORUM_SENTIMENTS) })).optional(),
    })
        .superRefine(refineContentBlock)
        .optional(),
    fameRiskDetail: z
        .object({
        ...blockBase,
        phases: z.array(z.object({ name: prose(200), period: prose(80), min: euro, max: euro })),
        notaCPA: prose(4000).optional(),
        comparatori: prose(4000).optional(),
        rischioGruppo: z.array(prose(1000)).optional(),
    })
        .optional(),
    /**
     * Facoltativi dal 16/10 (feedback del committente, Level 2 punto 3), come
     * già `sentiment`, `forum` e `fameRiskDetail`: quando il blocco manca la
     * card non si mostra, invece di far rifiutare il file. È un vincolo che si
     * allenta, quindi ogni file prodotto finora resta valido.
     */
    actionPlan: z
        .object({
        ...blockBase,
        areas: z.array(z.object({ name: prose(200), actions: z.array(prose(1000)) })),
    })
        .optional(),
    roadmap: z
        .object({
        ...blockBase,
        phases: z.array(z.object({ name: prose(200), period: prose(80), desc: prose(2000) })),
    })
        .optional(),
});
// ---------------------------------------------------------------------------
// Oggetto brand
// ---------------------------------------------------------------------------
/** §2 — `date`/`lastUpdated`: "GG/MM/AAAA" o "Luglio 2026", resi così come sono. */
const dateLike = z.string().min(4).max(40);
export const brandSchema = z
    .object({
    /** §2 — slug minuscolo con trattini, identico allo slug /verify/. */
    id: z
        .string()
        .min(2)
        .max(80)
        .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, { message: 'slug_non_valido' }),
    name: prose(200),
    url: z.string().max(300),
    sector: prose(120),
    sectorKey: z
        .string()
        .min(2)
        .max(60)
        .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, { message: 'sectorkey_non_valida' }),
    date: dateLike,
    lastUpdated: dateLike,
    signalQuality: z.enum(SIGNAL_QUALITIES),
    // Codice al posto del messaggio Zod: il pannello admin traduce le voci di
    // `admin.issues.*`, e un "Invalid option: expected one of…" resterebbe in
    // inglese in entrambe le lingue.
    famecertStatus: z
        .enum(FAMECERT_STATUSES, { error: 'famecert_status_non_valido' })
        .default('none'),
    famecertPhase: z.int().min(0).max(10),
    normFlags: z.object({
        widget: z.boolean(),
        reportAbuse: z.boolean(),
    }),
    primaryPlatform: z.enum(PLATFORM_IDS),
    platforms: z.array(platformSchema).min(1),
    indices: indicesSchema,
    fameRisk: fameRiskSchema,
    classification: classificationSchema,
    evidenze: z.array(prose(1000)),
    /** §6 — testo Recap del report L1, HTML inline consentito. */
    analysis: htmlBlock(),
    reputationalDamage: reputationalDamageSchema,
    history: z.array(historySnapshotSchema).min(1).max(MAX_HISTORY_SNAPSHOTS),
    level2: level2Schema,
    /**
     * v40 — contenuto del Level 3, reso nell'accordion "C1 — Di cosa si
     * lamentano i clienti". Quando manca, la vista mostra il testo di rinvio
     * "Analisi disponibile su richiesta".
     */
    level3: z.object({ extra: htmlBlock().optional() }).optional(),
})
    .superRefine((b, ctx) => {
    // §4 — un solo blocco per piattaforma.
    const ids = b.platforms.map((p) => p.id);
    const duplicati = ids.filter((id, i) => ids.indexOf(id) !== i);
    if (duplicati.length > 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['platforms'],
            message: `piattaforma_duplicata: ${[...new Set(duplicati)].join(', ')}`,
        });
    }
    // §2 — la piattaforma guida deve essere fra quelle del brand.
    if (!ids.includes(b.primaryPlatform)) {
        ctx.addIssue({
            code: 'custom',
            path: ['primaryPlatform'],
            message: 'primaryplatform_non_presente_in_platforms',
        });
    }
    // [CRITICA §3] FameIndex N.D.: il null va propagato a label, a tutti gli
    // snapshot storici e al gap, altrimenti il grafico disegna punti inventati.
    const indexIsNull = b.indices.fameIndex.value === null;
    if (indexIsNull) {
        if (b.indices.fameIndex.label !== 'N.D.') {
            ctx.addIssue({
                code: 'custom',
                path: ['indices', 'fameIndex', 'label'],
                message: 'label_deve_essere_nd',
            });
        }
        b.history.forEach((h, i) => {
            if (h.fameIndex !== null) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['history', i, 'fameIndex'],
                    message: 'nd_non_propagato',
                });
            }
            if (h.gap !== null) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['history', i, 'gap'],
                    message: 'nd_non_propagato_su_gap',
                });
            }
        });
    }
    else if (b.indices.fameIndex.label === 'N.D.') {
        ctx.addIssue({
            code: 'custom',
            path: ['indices', 'fameIndex', 'label'],
            message: 'label_nd_con_valore_presente',
        });
    }
    // [CRITICA §8.3] Cadenza coerente entro il brand: il grafico tratta i punti
    // come equidistanti, mescolare trimestri e mesi falserebbe la pendenza.
    const cadenze = new Set(b.history.map((h) => (QUARTER.test(h.month) ? 'trimestrale' : 'mensile')));
    if (cadenze.size > 1) {
        ctx.addIssue({
            code: 'custom',
            path: ['history'],
            message: 'cadenza_storico_mista',
        });
    }
    // §8 — nessuno snapshot ripetuto.
    const mesi = b.history.map((h) => h.month);
    if (new Set(mesi).size !== mesi.length) {
        ctx.addIssue({ code: 'custom', path: ['history'], message: 'snapshot_duplicati' });
    }
});
/**
 * Campi usciti dalla specifica, come percorsi (`[]` = ogni elemento
 * dell'array). Un file che li porta ancora riceve l'avviso non bloccante
 * `removed_fields_present` e il campo non viene salvato: probabile conversione
 * con un template vecchio. Unico elenco per `findRemovedFields` e per
 * `findDroppedFields`, che li salta per non segnalarli due volte.
 */
export const REMOVED_FIELD_PATHS = [
    'fameRating', // v2.1 §3
    'indices.fameRating',
    'platforms[].basicLinkLevel', // v2.1 §4
    'indices.fameIndex.color', // v2.5: derivato dal value
    'fameRisk.color', // v2.5: derivato dal livello
    'normFlags.customWidget', // v2.5: mai letto
    'aggravanti', // v2.5: mai letto, il blocco 08 deriva i fattori
];
const REMOVED_FIELD_SET = new Set(REMOVED_FIELD_PATHS);
/** Campi rimossi dalla specifica presenti nel grezzo, nell'ordine di `REMOVED_FIELD_PATHS`. */
export function findRemovedFields(raw) {
    return REMOVED_FIELD_PATHS.filter((path) => hasPath(raw, path.split('.')));
}
function hasPath(value, segments) {
    if (segments.length === 0)
        return true;
    const [head, ...rest] = segments;
    if (head.endsWith('[]')) {
        const arr = isPlainObject(value) ? value[head.slice(0, -2)] : undefined;
        return Array.isArray(arr) && arr.some((item) => hasPath(item, rest));
    }
    if (!isPlainObject(value) || !(head in value))
        return false;
    return hasPath(value[head], rest);
}
/**
 * Chiavi del JSON in arrivo che lo schema non dichiara e che `z.object`
 * scarterebbe in silenzio: confronto ricorsivo fra grezzo e parsed, per l'avviso
 * non bloccante. Salta `platforms[].intelligence` (schema-less, §4bis) e i campi
 * già segnalati da `findRemovedFields`.
 */
export function findDroppedFields(raw, parsed) {
    const found = [];
    walk(raw, parsed, '', found);
    return found;
}
function walk(raw, parsed, path, found) {
    if (Array.isArray(raw)) {
        if (!Array.isArray(parsed))
            return;
        raw.forEach((item, i) => walk(item, parsed[i], `${path}[${i}]`, found));
        return;
    }
    if (!isPlainObject(raw) || !isPlainObject(parsed))
        return;
    for (const [key, value] of Object.entries(raw)) {
        const childPath = path === '' ? key : `${path}.${key}`;
        if (!(key in parsed)) {
            // Il confronto è per percorso, non per nome: `reputationalDamage.note` o
            // un `color` in un'altra posizione restano da segnalare.
            if (!REMOVED_FIELD_SET.has(childPath.replace(/\[\d+\]/g, '[]')))
                found.push(childPath);
            continue;
        }
        // Sottoalbero schema-less: tutto sopravvive, non c'è nulla da confrontare.
        if (key === 'intelligence')
            continue;
        walk(value, parsed[key], childPath, found);
    }
}
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
//# sourceMappingURL=brand.js.map